import type { ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  CircleUserRound,
  Key,
  FlaskConical,
  ChartColumn,
  User,
  Lock,
  Users,
  LogOut,
  type LucideIcon,
} from 'lucide-react'
import { SiteLayout } from '@/components/site/SiteLayout'
import { useAuth } from '@/lib/auth'
import { useActiveOrg } from '@/lib/user-profile'

// =============================================================================
// AccountLayout
//
// Shared chrome for every /account/* page: left rail with the four account
// sections + sign-out at the bottom, content area on the right.
//
// At md+ the rail is a persistent column. Below md it collapses to a
// horizontal scroll strip above the content so the sections stay
// reachable without dominating the viewport.
//
// Keys is intentionally first — it's the highest-traffic section and the
// user explicitly asked for that ordering.
// =============================================================================

interface AccountNavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Returns true if the current pathname should highlight this entry. */
  matches: (pathname: string) => boolean
}

const ACCOUNT_NAV: ReadonlyArray<AccountNavItem> = [
  // /account is the Overview/landing — its own entry, not collapsed
  // into the API Keys item. The two pages are distinct concepts: the
  // overview surfaces "your account" context (active org + key/usage
  // glance), while API Keys is the management page.
  {
    to: '/account',
    label: 'Account',
    icon: CircleUserRound,
    matches: (p) => p === '/account',
  },
  {
    to: '/account/keys',
    label: 'API Keys',
    icon: Key,
    matches: (p) => p.startsWith('/account/keys'),
  },
  // Right after API Keys: the playground needs a key, so they pair —
  // generate a key, then try it next door.
  {
    to: '/account/playground',
    label: 'Playground',
    icon: FlaskConical,
    matches: (p) => p.startsWith('/account/playground'),
  },
  {
    to: '/account/reporting',
    label: 'Reporting',
    icon: ChartColumn,
    matches: (p) => p.startsWith('/account/reporting'),
  },
  {
    to: '/account/profile',
    label: 'Profile',
    icon: User,
    matches: (p) => p.startsWith('/account/profile'),
  },
  {
    to: '/account/security',
    label: 'Security',
    icon: Lock,
    matches: (p) => p.startsWith('/account/security'),
  },
  {
    to: '/account/org',
    label: 'Organization',
    icon: Users,
    matches: (p) => p.startsWith('/account/org'),
  },
]

interface AccountLayoutProps {
  children: ReactNode
}

export function AccountLayout({ children }: AccountLayoutProps) {
  const { pathname } = useLocation()
  const { signOut } = useAuth()
  const navigate = useNavigate()
  // Reporting is owner/admin-only (it shows org-wide usage + source IPs across
  // all keys). Hide the nav item for plain members; the route is gated too.
  const { org } = useActiveOrg()
  const isOrgAdmin = org?.caller_role === 'owner' || org?.caller_role === 'admin'
  const navItems = ACCOUNT_NAV.filter(
    (item) => item.to !== '/account/reporting' || isOrgAdmin,
  )

  async function onSignOut() {
    await signOut()
    // ?logged-out=1 surfaces a "You've been signed out" banner on
    // /sign-in so the action has visible confirmation.
    navigate('/sign-in?logged-out=1', { replace: true })
  }

  return (
    <SiteLayout>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14">
        <div className="grid gap-8 md:grid-cols-[220px_1fr]">
          {/* Rail */}
          <aside aria-label="Account sections" className="md:sticky md:top-8 md:self-start">
            {/* md+ : vertical column. <md : horizontal scroll strip so all
                items stay reachable without folding into a menu.
                No eyebrow header — the visible nav items are self-explanatory
                and the rail is already in the /account context. */}
            <nav className="flex flex-row gap-1 overflow-x-auto md:flex-col md:overflow-visible">
              {navItems.map((item) => {
                const active = item.matches(pathname)
                const Icon = item.icon
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={[
                      'inline-flex items-center gap-2.5 px-3 py-2 rounded-[var(--radius-DEFAULT)]',
                      'text-[13.5px] font-medium whitespace-nowrap',
                      'transition-colors cursor-pointer',
                      active
                        ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
                        : 'text-[var(--color-muted)] hover:bg-[var(--color-elev)] hover:text-[var(--color-text)]',
                    ].join(' ')}
                  >
                    <Icon size={15} strokeWidth={2.2} aria-hidden />
                    {item.label}
                  </Link>
                )
              })}
              {/* Sign out is always rendered (no md:-gated hidden). On
                  desktop (md+) it sits at the bottom of the vertical rail
                  separated by mt-4. On mobile the rail is a horizontal
                  scroll strip and the button sits inline at the end —
                  which matters because the site header's own Sign-out is
                  itself hidden below sm (640px), so this is the only
                  visible logout on phones. */}
              <button
                type="button"
                onClick={onSignOut}
                className={[
                  'inline-flex items-center gap-2.5 px-3 py-2 md:mt-4',
                  'text-[13px] text-[var(--color-muted)]',
                  'hover:text-[var(--color-signal)] hover:bg-[var(--color-signal-soft)]',
                  'rounded-[var(--radius-DEFAULT)] transition-colors cursor-pointer whitespace-nowrap',
                ].join(' ')}
              >
                <LogOut size={14} aria-hidden />
                Sign out
              </button>
            </nav>
          </aside>

          {/* Content */}
          <main className="min-w-0">{children}</main>
        </div>
      </div>
    </SiteLayout>
  )
}
